import React, { useEffect, useRef, useState } from "react";
import { AuthField } from "./AuthLayout";
import Icon from "./Icon";

const RECIPIENT = "mandate@pluginlive.com";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_MESSAGE = 1000;
const EMPTY = { name: "", email: "", mobile: "", org: "", message: "" };

function validate(f) {
  const e = {};
  if (!f.name.trim()) e.name = "Enter your full name";
  if (!f.email.trim()) e.email = "Enter your email";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email, like you@company.com";
  const digits = f.mobile.replace(/\D/g, "");
  if (f.mobile.trim() && (digits.length < 10 || digits.length > 13 || /[^\d+\s()-]/.test(f.mobile))) {
    e.mobile = "Enter a valid mobile number";
  }
  if (!f.org.trim()) e.org = "Enter your company or institute name";
  return e;
}

/**
 * "New to PluginLive? Contact us" — an enquiry for the PluginLive team.
 * Native <dialog> (showModal) gives the focus trap, Esc-to-close and top layer.
 * ponytail: hands off to the visitor's mail app via mailto (the design lane
 * can't add a mail-sending endpoint, same as the candidate app's Get Support).
 * Ceiling: a device with no mail app opens nothing. Upgrade: POST to a public
 * enquiry endpoint that mails RECIPIENT server-side, and show the sent state
 * from its response.
 */
export default function ContactDialog({ open, onClose }) {
  const ref = useRef(null);
  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [handedOff, setHandedOff] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setFields(EMPTY);
      setErrors({});
      setHandedOff(false);
      d.showModal();
    } else if (!open && d.open) d.close();
  }, [open]);

  const set = (key) => (e) => {
    const { value } = e.target;
    setFields((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: undefined }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const found = validate(fields);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`contact-${first}`)?.focus();
      return;
    }
    const f = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.trim()]));
    const subject = `New enquiry — ${f.org}`;
    const body = [
      `Full name: ${f.name}`,
      `Email: ${f.email}`,
      `Mobile: ${f.mobile || "—"}`,
      `Company / institute: ${f.org}`,
      "",
      f.message || "(no message)",
    ].join("\n");
    window.location.href = `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setHandedOff(true);
  };

  const input = (key, props) => (
    <input
      id={`contact-${key}`}
      value={fields[key]}
      onChange={set(key)}
      aria-invalid={errors[key] ? "true" : undefined}
      aria-describedby={errors[key] ? `contact-${key}-error` : undefined}
      {...props}
    />
  );
  const optional = (text) => <>{text}<span className="auth-opt">optional</span></>;

  return (
    <dialog
      ref={ref}
      className="contact-dialog"
      aria-labelledby="contact-title"
      onClose={onClose}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="cd-head">
        <h2 id="contact-title">{handedOff ? "Almost done" : "Contact us"}</h2>
        <button type="button" className="cd-close" aria-label="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>

      {handedOff ? (
        <>
          <div className="cd-body">
            <p className="cd-sub">
              Your email app has opened with your message to <strong>{RECIPIENT}</strong>. Press <strong>Send</strong> there and our team will get back to you.
            </p>
          </div>
          <div className="cd-foot">
            <button type="button" className="auth-btn auth-btn-primary" onClick={onClose}>Done</button>
          </div>
        </>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <div className="cd-body">
            <p className="cd-sub">Tell us a little about you and our team will get in touch.</p>
            <AuthField id="contact-name" label="Full name" error={errors.name}>
              {input("name", { type: "text", autoComplete: "name", maxLength: 120, placeholder: "e.g. Priya Sharma" })}
            </AuthField>
            <AuthField id="contact-email" label="Email" error={errors.email}>
              {input("email", { type: "email", inputMode: "email", autoComplete: "email", maxLength: 254, placeholder: "you@company.com" })}
            </AuthField>
            <AuthField id="contact-mobile" label={optional("Mobile")} error={errors.mobile}>
              {input("mobile", { type: "tel", inputMode: "tel", autoComplete: "tel", maxLength: 20, placeholder: "e.g. 98765 43210" })}
            </AuthField>
            <AuthField id="contact-org" label="Company or institute name" error={errors.org}>
              {input("org", { type: "text", autoComplete: "organization", maxLength: 160, placeholder: "e.g. Acme Corp or ABC College" })}
            </AuthField>
            <AuthField id="contact-message" label={optional("Message")}>
              <textarea
                id="contact-message"
                rows={3}
                maxLength={MAX_MESSAGE}
                value={fields.message}
                onChange={set("message")}
                placeholder="What would you like to know?"
              />
            </AuthField>
          </div>
          <div className="cd-foot">
            <button type="button" className="auth-btn auth-btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="auth-btn auth-btn-primary">Send</button>
          </div>
        </form>
      )}
    </dialog>
  );
}
